import { useEffect, useState } from 'react';
import api from '../utils/api';
import { FALLBACK_CATEGORIES, normalizeCategory } from '../constants/categories';

/**
 * useCategories — the admin-managed storefront catalogue categories.
 *
 * Categories live in the Categories table and are edited in /admin/categories.
 * The public read is GET /api/categories. Mirrors useRewardConfig's module-scope
 * cache so the navbar dropdown, Shop filters and homepage carousels share one
 * fetch per session.
 *
 * After an admin adds/edits/removes a category, call `refreshCategories()` to
 * re-fetch and push the new list into every mounted consumer — so the Catalogue
 * dropdown updates without a page reload.
 *
 * Falls back to the historic "big 4" so every consumer renders on first paint
 * and if the request ever fails.
 */
let cache = null;            // last successful fetch (shared across consumers)
let inflight = null;         // de-dupes concurrent first loads
const listeners = new Set(); // mounted consumers' setState fns

function extract(data) {
  const list = Array.isArray(data) ? data : (data?.data || []);
  return list.map(normalizeCategory);
}

async function loadCategories(force = false) {
  if (cache && !force) return cache;
  if (!inflight) {
    inflight = api.get('/categories')
      .then(({ data }) => {
        const list = extract(data);
        cache = list.length ? list : [...FALLBACK_CATEGORIES];
        return cache;
      })
      .catch(() => (cache || [...FALLBACK_CATEGORIES]))
      .finally(() => { inflight = null; });
  }
  return inflight;
}

/** Re-fetch and broadcast to every mounted consumer (call after admin edits). */
export async function refreshCategories() {
  const list = await loadCategories(true);
  listeners.forEach((fn) => fn(list));
  return list;
}

export function useCategories() {
  const [categories, setCategories] = useState(cache || FALLBACK_CATEGORIES);

  useEffect(() => {
    let active = true;
    const update = (list) => { if (active) setCategories(list); };
    listeners.add(update);
    loadCategories().then(update);
    return () => { active = false; listeners.delete(update); };
  }, []);

  const byValue = Object.fromEntries(categories.map((c) => [c.value, c]));
  const label = (value) => byValue[value]?.label || value || '';

  return { categories, byValue, label };
}
